import { EyeOutlined } from '@ant-design/icons';
import { type IGiftRegistryAdminRefundItem } from '@mahawi/eshop-common/src/types';
import { Button, Space, Tag, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import GiftRegistryRefundContributionsDrawer from '../components/gift-registry/gift-registry-refund-contributions-drawer';
import TableComponent from '../components/table';
import UpdatedAt from '../components/updated-at';
import { EDateFormat } from '../fragments/date-format';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { giftRegistryRefundItemsLoad } from '../reducers/gift-registry/reducer';
import { SPACING } from '../theme/tokens';

type TGiftRegistryRefundRow = IGiftRegistryAdminRefundItem & { key: string };

function GiftRegistryRefundsContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const items = useSelector((s: RootState) => s.GiftRegistry.refundItems);
  const inProcess = useSelector((s: RootState) => s.GiftRegistry.inProcess);
  const updatedAt = useSelector((s: RootState) => s.GiftRegistry.updatedAt);

  const [selectedItemUUID, setSelectedItemUUID] = useState<string | null>(null);

  useEffect(() => {
    dispatch(giftRegistryRefundItemsLoad());
  }, [dispatch]);

  const selectedItem = useMemo(
    () => items?.find((item) => item.item.uuid === selectedItemUUID) ?? null,
    [items, selectedItemUUID],
  );

  useEffect(() => {
    if (selectedItemUUID && !selectedItem) {
      setSelectedItemUUID(null);
    }
  }, [selectedItemUUID, selectedItem]);

  const dataSource = useMemo<TGiftRegistryRefundRow[]>(
    () => (items ?? []).map((item) => ({ key: item.item.uuid, ...item })),
    [items],
  );

  const columns = useMemo<ColumnsType<TGiftRegistryRefundRow>>(
    () => [
      {
        title: 'Registry',
        dataIndex: ['giftRegistry', 'title'],
      },
      {
        title: 'Owner',
        render: (
          _text: unknown,
          record: TGiftRegistryRefundRow,
        ): React.ReactElement => (
          <>
            <Typography.Text>
              {record.giftRegistry.ownerDisplayName}
            </Typography.Text>
            <br />
            <Typography.Text type="secondary">
              {record.giftRegistry.ownerAccountEmail}
            </Typography.Text>
          </>
        ),
      },
      {
        title: 'Product',
        dataIndex: ['item', 'product', 'name'],
        render: (name: string | null, record: TGiftRegistryRefundRow): string =>
          name ?? record.item.product.uuid,
      },
      {
        title: 'Captured',
        render: (
          _text: unknown,
          record: TGiftRegistryRefundRow,
        ): React.ReactElement => {
          const total = record.contributions.reduce(
            (sum, contribution) => sum + contribution.amount,
            0,
          );

          return (
            <Tag color="orange">
              {total} {record.currencyType.isoCode} (
              {record.contributions.length} contribution
              {record.contributions.length === 1 ? '' : 's'})
            </Tag>
          );
        },
      },
      {
        title: 'Registry expired',
        dataIndex: ['giftRegistry', 'expiresAt'],
        sorter: (
          a: TGiftRegistryRefundRow,
          b: TGiftRegistryRefundRow,
        ): number =>
          dayjs(a.giftRegistry.expiresAt).unix() -
          dayjs(b.giftRegistry.expiresAt).unix(),
        render: (expiresAt: Date): string =>
          dayjs(expiresAt).format(EDateFormat.DATE),
      },
      {
        title: 'Actions',
        render: (
          _text: unknown,
          record: TGiftRegistryRefundRow,
        ): React.ReactElement => (
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={(): void => setSelectedItemUUID(record.item.uuid)}
          >
            View contributions
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title="Gift registry refunds"
        onReload={(): void => {
          dispatch(giftRegistryRefundItemsLoad());
        }}
        isReloadDisabled={inProcess}
      />

      <Typography.Text type="secondary">
        Registries that expired without being confirmed by the owner, still
        holding captured contributions. Refund donors manually in the Comgate
        dashboard, then mark each contribution as refunded here.{' '}
        <UpdatedAt value={updatedAt} />
      </Typography.Text>

      <TableComponent
        columns={columns as Parameters<typeof TableComponent>[0]['columns']}
        dataSource={dataSource}
        isLoading={inProcess && !items}
        emptyText="Nothing awaiting a manual refund"
      />

      <GiftRegistryRefundContributionsDrawer
        open={!!selectedItem}
        onClose={(): void => setSelectedItemUUID(null)}
        item={selectedItem}
      />
    </Space>
  );
}

export default GiftRegistryRefundsContainer;
